import { useId, useState, type Ref } from 'react';
import type { FilmStock } from '../api/filmStocks';
import type { RollFormat, RollKind, RollProcess } from '../types/roll';
import { stockLabel, UNKNOWN_FILM_STOCK } from '../utils/filmStocks';
import { FORMAT_LABELS, KIND_LABELS, labNumberRequired, PROCESS_LABELS } from '../utils/rollDetails';
import { AddFilmStockDialog } from './AddFilmStockDialog';
import { FilmStockPicker } from './FilmStockPicker';
import styles from './RollDetailsFields.module.css';

/** One roll's details as the form holds them. */
export interface RollFieldsValue {
  number: string;
  stock: FilmStock | null;
  kind: RollKind;
  format: RollFormat | '';
  process: RollProcess | '';
}

export interface RollFieldErrors {
  number?: string;
  stock?: string;
  format?: string;
}

export interface RollDetailsFieldsProps {
  value: RollFieldsValue;
  onChange: (patch: Partial<RollFieldsValue>) => void;
  errors?: RollFieldErrors;
  stocks: FilmStock[];
  /** The lab number input, so a form can focus it. */
  numberRef?: Ref<HTMLInputElement>;
}

/**
 * A roll's lab number, film stock, kind, format and process. Choosing a stock
 * sets the process to the stock's; "Add ‘…’ as a new stock" adds one in a
 * dialog without leaving the form.
 */
export function RollDetailsFields({ value, onChange, errors = {}, stocks, numberRef }: RollDetailsFieldsProps) {
  const id = useId();
  const [adding, setAdding] = useState<string | null>(null);
  // Remounts the picker to show a stock chosen outside it (in the dialog).
  const [pickerKey, setPickerKey] = useState(0);
  const required = labNumberRequired(value.format);

  function choose(stock: FilmStock) {
    const known = stock.stock_id !== UNKNOWN_FILM_STOCK.stock_id;
    onChange({ stock, process: known ? stock.process : value.process });
  }

  const errorId = (field: keyof RollFieldErrors) => `${id}-${field}-error`;

  return (
    <div className={styles.fields}>
      <div className={styles.field}>
        <label htmlFor={`${id}-number`} className={styles.label}>
          Lab number
        </label>
        <input
          ref={numberRef}
          id={`${id}-number`}
          className={`${styles.input} ${styles.number}`}
          value={value.number}
          onChange={(e) => onChange({ number: e.target.value })}
          autoComplete="off"
          spellCheck={false}
          maxLength={16}
          aria-invalid={errors.number ? true : undefined}
          aria-describedby={errors.number ? errorId('number') : !required ? `${id}-number-hint` : undefined}
        />
        {errors.number ? (
          <p id={errorId('number')} className={styles.error}>
            {errors.number}
          </p>
        ) : (
          !required && (
            <p id={`${id}-number-hint`} className={styles.hint}>
              Optional for 120
            </p>
          )
        )}
      </div>

      <div className={`${styles.field} ${styles.stock}`}>
        <FilmStockPicker
          key={pickerKey}
          label="Film stock"
          stocks={stocks}
          includeUnknown
          initialLabel={value.stock ? stockLabel(value.stock) : ''}
          onPick={choose}
          onAddNew={setAdding}
          invalid={!!errors.stock}
          errorId={errors.stock ? errorId('stock') : undefined}
        />
        {errors.stock && (
          <p id={errorId('stock')} className={styles.error}>
            {errors.stock}
          </p>
        )}
      </div>

      <div className={styles.field}>
        <label htmlFor={`${id}-kind`} className={styles.label}>
          Service
        </label>
        <select
          id={`${id}-kind`}
          className={styles.input}
          value={value.kind === 'unknown' ? 'develop_and_scan' : value.kind}
          onChange={(e) => onChange({ kind: e.target.value as RollKind })}
        >
          {Object.entries(KIND_LABELS).map(([kind, label]) => (
            <option key={kind} value={kind}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label htmlFor={`${id}-format`} className={styles.label}>
          Format
        </label>
        <select
          id={`${id}-format`}
          className={styles.input}
          value={value.format}
          onChange={(e) => onChange({ format: e.target.value as RollFormat | '' })}
          aria-invalid={errors.format ? true : undefined}
          aria-describedby={errors.format ? errorId('format') : undefined}
        >
          <option value="">Choose…</option>
          {Object.entries(FORMAT_LABELS).map(([format, label]) => (
            <option key={format} value={format}>
              {label}
            </option>
          ))}
        </select>
        {errors.format && (
          <p id={errorId('format')} className={styles.error}>
            {errors.format}
          </p>
        )}
      </div>

      <div className={styles.field}>
        <label htmlFor={`${id}-process`} className={styles.label}>
          Process
        </label>
        <select
          id={`${id}-process`}
          className={styles.input}
          value={value.process}
          onChange={(e) => onChange({ process: e.target.value as RollProcess | '' })}
        >
          <option value="">As the film stock</option>
          {Object.entries(PROCESS_LABELS).map(([process, label]) => (
            <option key={process} value={process}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {adding !== null && (
        <AddFilmStockDialog
          query={adding}
          stocks={stocks}
          onChosen={(stock) => {
            choose(stock);
            setPickerKey((k) => k + 1);
            setAdding(null);
          }}
          onClose={() => setAdding(null)}
        />
      )}
    </div>
  );
}
